import { cloneDeep, compact, flatten, has, isEmpty, isNaN, uniq } from 'lodash';
import { type MouseEventHandler, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  type LegendPayload,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { LabelPosition } from 'recharts/types/component/Label';
import type { CartesianLabelListEntry, PolarLabelListEntry } from 'recharts/types/component/LabelList';
import { MIN_COLUMN_BAR_HEIGHT } from '../../constants/widget-configuration';
import { ColumnChartCommandFactory } from '../../data-transformer/column-chart/column-chart-command.factory';
import type { FieldDataProps } from '../../data-transformer/column-chart/column-chart-interfaces';
import { DataExecutor } from '../../data-transformer/data-executor';
import type { DataCommandResponse } from '../../data-transformer/data-transformer.interface';
import { getColorsArray } from '../../helpers/get-colors-arrays';
import { getStatusColor } from '../../helpers/getStatusColor';
import { getValueOrDefault } from '../../helpers/getvalueOrDefault';
import { handleMouseEnter, handleMouseLeave } from '../../helpers/tooltip-functions';
import { type BasicDataChartResponse, useColumnChartQuery } from '../../redux/config/chart-data';
import { isEmptyValue } from '../../redux/config/details-window';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import type { ToolTipElement } from '../../shared/widget-data.interfaces';
import OdinCustomDetailsWindow, { type CustomModalProps } from '../CustomDetailsWindow/custom-details-window.component';
import CustomTooltip from '../CustomToolTip/CustomToolTip';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import CustomizedLabel from '../common/column-chart-custom-label.component';
import { renderCustomizedLineLabel } from '../common/custom-label-list';
import { CustomLegendWithCustomScroll, defaultLegendWrapperStyle } from '../common/custom-legend.component';
import { CustomMiddleLabelList } from '../common/custom-middle-label.component';
import { CustomValueAccessor } from '../common/custom-value-accessor';
import { CustomXAxis, XAxisLineChart } from '../common/custom-X-axis.component';
import { ActiveDot } from '../common/line-active-dot.component';
import { getDotWithColor } from '../common/line-dot-base-props';
import { sortOrderStrings } from '../common/sort-order-strings';
import { yAxisSettings } from '../common/y-axis-properties';
import type { CustomColorProps } from '../WidgetSettingWindow/components/config-components.interfaces';
import type { ChartProps } from './chart.interface';
import { findCustomColor, includeLegend, intervalCounter } from './generalFunctions/general-functions';
import { generateTicks } from './generalFunctions/generate-ticks';
import { filterChartStackedColumnValueByLegend, getCustomYScaleDomainValue } from './generalFunctions/get-domaincharts';
import { useXAxisTopSettings } from './hooks/use-x-axis-top-settings';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import { useKpiConfigs } from './hooks/useKPIConfigs';
import { useKPIDomainValue } from './hooks/useKPIDomainValue';
import { useFRConfigs } from './hooks/useRollingFRConfigs';
import { useTRIFRConfigs } from './hooks/useRollingTRIFRConfigs';
import useStartEndDates from './hooks/useStartEndDates';
import { createColumnChartBarClickHandler, wrapPayloadChartContent, wrapToolTipElementContent } from './recharts-types';

export interface ColumnChartCustomModalProps extends CustomModalProps {
  Sum?: string;
}

export default function OdinColumnChart({ widgetId, layoutProps }: ChartProps) {
  const [transformedData, setTransformedData] = useState<Array<FieldDataProps>>();
  const [legendProps, setLegendProps] = useState<Record<string, boolean>>({});
  const [modalData, setModalData] = useState<ColumnChartCustomModalProps>();
  const [barChartKeys, setBarChartKeys] = useState<Array<string>>([]);
  const [rollingSiteTRIFRKeys, setRollingSiteTRIFRKeys] = useState<Array<string>>([]);
  const [rollingFRKeys, setRollingFRKeys] = useState<Array<string>>([]);
  const [detailsWindowVisible, setDetailsWindowVisible] = useState<boolean>(false);
  const [xLabelsHeight, setXLabelsHeight] = useState<number>(30);
  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();

  const chartParentRef = useRef<HTMLDivElement>(null);

  const { startDate, endDate } = useStartEndDates();
  const widgets = useSelector(getWidgets);

  const {
    data: widgetChartData,
    isLoading: isWidgetChartDataLoading,
    isFetching: isWidgetChartDataFetching,
    error,
    isError,
  } = useColumnChartQuery({
    widgetId,
    from: startDate,
    to: endDate,
  });

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  const recalculateLabelsHeight = useCallback(() => {
    const targetNode = chartParentRef.current;
    if (targetNode) {
      const xAxis = targetNode.getElementsByClassName(`recharts-xAxis`)[0];
      if (xAxis) {
        const height = Math.max(30, Math.ceil((xAxis as SVGSVGElement).getBBox().height));
        setXLabelsHeight((previousHeight) => (previousHeight === height ? previousHeight : height));
      }
    }
  }, []);

  const xAxisSettings = useXAxisTopSettings('name', 'category');
  const yAxisLineName = useRef<string>('lineYAxis');

  const kpiConfigs = useKpiConfigs(configurationData);
  const renderKPILine = useMemo(
    () => has(widgetChartData, 'kpi') && kpiConfigs.available,
    [kpiConfigs, widgetChartData]
  );
  const trifrConfig = useTRIFRConfigs(configurationData);
  const renderTrifrLine = useMemo(
    () => has(widgetChartData, 'rollingTRIFR') && trifrConfig.available,
    [trifrConfig, widgetChartData]
  );
  const frConfig = useFRConfigs(configurationData);
  const renderRollingFR = useMemo(
    () => has(widgetChartData, 'rollingFR') && frConfig.available,
    [widgetChartData, frConfig]
  );
  const renderRollingSiteTRIFR: boolean = useMemo(
    () => has(widgetChartData, 'rollingSiteTRIFR') && configurationData?.widgetData?.rollingTRIFR !== undefined,
    [widgetChartData, configurationData]
  );

  const basicValue = useMemo(() => {
    if (widgetChartData) {
      return widgetChartData.basicValue;
    }
    return [];
  }, [widgetChartData]);

  const isSumCalculationType = useMemo(() => {
    return configurationData && configurationData.widgetData?.calculationType === 'sum';
  }, [configurationData]);

  const colors = useMemo(() => {
    let allValidKeys: string[] | CustomColorProps[] = [];
    if (basicValue) {
      if (isSumCalculationType) {
        const widgetConfiguration = configurationData?.widgetConfig;

        allValidKeys = uniq(
          basicValue.map((item) => {
            const { field, secondField, value } = item;
            const secondColorValue =
              secondField != null && secondField !== '' && !isNaN(Number(secondField))
                ? Number(secondField)
                : Number(value);
            const isSecondColorActive =
              widgetConfiguration?.secondColorEnabled &&
              Number(widgetConfiguration?.threshold) <= Number(secondColorValue);
            const color =
              widgetConfiguration &&
              (isSecondColorActive ? widgetConfiguration.secondColor : widgetConfiguration.defaultColor);
            const property: CustomColorProps = {
              label: getValueOrDefault(field, '[none]'),
              color: getValueOrDefault(color, getStatusColor(0)),
            };

            return property;
          })
        );
      } else if (!isSumCalculationType) {
        allValidKeys = uniq(
          basicValue.map((item) => {
            const { field, secondField } = item;
            return getValueOrDefault(secondField ?? field, '[none]');
          })
        );
      }
    }

    const colors = getColorsArray(allValidKeys, configurationData);
    // if sortOrder is not present in configurationData, sort colors alphabetically by label
    if (!configurationData?.widgetConfig?.sortOrder) {
      return (colors as CustomColorProps[]).sort((a, b) => a.label.localeCompare(b.label));
    }
    return colors;
  }, [configurationData, basicValue, isSumCalculationType]);

  useEffect(() => {
    if (widgetChartData && configurationData && !isWidgetChartDataFetching) {
      const commandExecutor = new DataExecutor();
      const commandFactory = new ColumnChartCommandFactory(configurationData);
      let ColumnChartCommandResponse: DataCommandResponse<unknown, unknown, Array<FieldDataProps>> | undefined;
      let outputData: undefined | Array<FieldDataProps>;

      while ((ColumnChartCommandResponse = commandFactory.getNextCommand())) {
        const inputData = outputData || widgetChartData;
        outputData = commandExecutor.execute(ColumnChartCommandResponse.command, inputData, configurationData);
      }

      if (outputData) {
        if (basicValue) {
          // Second field is present when chart is stacked and must be used then
          const getMappedValue = (item: BasicDataChartResponse) => {
            if ('secondField' in item && !isSumCalculationType) {
              return getValueOrDefault(item.secondField, '[none]');
            }
            if (isSumCalculationType) {
              return 'Sum';
            }
            return 'Quantity';
          };

          let basicValueKeys = uniq(flatten(compact(basicValue.map(getMappedValue))));

          // this chunk of codes will add sortOrder to the configurationData if it is not present
          const newConfigurationData = cloneDeep(configurationData);
          if (!configurationData.widgetConfig?.sortOrder) {
            newConfigurationData.widgetConfig = newConfigurationData.widgetConfig || undefined;
            if (newConfigurationData.widgetConfig) {
              // put [none] value last if sortOrder is not present, similar to OD
              const sortedColors = colors
                .slice()
                .sort((a, b) => (a.label === '[none]' ? 1 : b.label === '[none]' ? -1 : 0));
              newConfigurationData.widgetConfig.sortOrder = sortedColors.map((item) => item.label);
            }
          }

          if (configurationData.widgetConfig?.hideBlankCategory) {
            basicValueKeys = basicValueKeys.filter((key) => !isEmptyValue(key));
          }

          setBarChartKeys(sortOrderStrings(outputData, newConfigurationData, basicValueKeys));
        }
        if (widgetChartData.rollingSiteTRIFR) {
          setRollingSiteTRIFRKeys(Object.keys(widgetChartData.rollingSiteTRIFR));
        }
        if (widgetChartData.rollingFR) {
          setRollingFRKeys(Object.keys(widgetChartData.rollingFR));
        }
        setTransformedData(outputData);
      }
    }
  }, [widgetChartData, configurationData, isSumCalculationType, basicValue, isWidgetChartDataFetching, colors]);

  const onShowModal = (data: ColumnChartCustomModalProps) => {
    setModalData(data);
    setDetailsWindowVisible(true);
  };

  const onDialogHidden = () => setDetailsWindowVisible(false);

  const selectItem = (dataKey: string) =>
    setLegendProps({
      ...legendProps,
      [dataKey || '']: !legendProps[dataKey || ''],
    });

  const hasAnyDataKey = useMemo(() => {
    if (widgetChartData) {
      return (
        'rollingFR' in widgetChartData ||
        'rollingTRIFR' in widgetChartData ||
        'rollingSiteTRIFR' in widgetChartData ||
        (basicValue?.length > 0 && 'basicValue' in widgetChartData) ||
        'kpi' in widgetChartData
      );
    }
  }, [widgetChartData, basicValue]);

  useLayoutEffect(() => {
    const targetNode = chartParentRef.current;
    if (!targetNode) return;

    const resizeObserver = new ResizeObserver(() => {
      recalculateLabelsHeight();
    });
    resizeObserver.observe(targetNode);

    return () => resizeObserver.disconnect();
  }, [recalculateLabelsHeight]);

  const filteredChartData = useMemo(() => {
    return filterChartStackedColumnValueByLegend(transformedData, legendProps);
  }, [transformedData, legendProps]);

  const linesYDomainScale: [number, number] | undefined = useKPIDomainValue(configurationData, filteredChartData);

  const renderYAxisKPIs: boolean = useMemo(() => {
    return (
      (renderKPILine || renderTrifrLine || renderRollingFR || renderRollingSiteTRIFR) &&
      (configurationData?.widgetConfig?.kpiTargetConfiguration?.onFRAxis ?? true)
    );
  }, [
    renderKPILine,
    renderTrifrLine,
    renderRollingFR,
    renderRollingSiteTRIFR,
    configurationData?.widgetConfig?.kpiTargetConfiguration?.onFRAxis,
  ]);

  const shouldSkipRenderingChart =
    !transformedData || transformedData.length === 0 || !hasAnyDataKey || (!renderYAxisKPIs && isEmpty(basicValue));

  const barChartYDomainValue: [number, number] | undefined = useMemo(() => {
    if (filteredChartData && configurationData) {
      const fieldColumn = 'value_column';
      const widgetConfig = configurationData.widgetConfig;
      const firstItem = filteredChartData[0];

      // Handle maxValue (always based on value_column)
      const maxValue = Math.max(
        ...filteredChartData.map((item) =>
          item[fieldColumn as keyof typeof item] ? Number(item[fieldColumn as keyof typeof item]) : 0
        )
      );

      let minValue: number;

      if (filteredChartData.length === 1 && firstItem) {
        // Get all numeric values from keys except 'value_column'
        const numericValues = Object.entries(firstItem)
          .filter(([key, value]) => key !== fieldColumn && typeof value === 'number')
          .map(([, value]) => Number(value));

        minValue = Math.min(...numericValues);
      } else {
        // Normal case: use value_column
        minValue = Math.min(
          ...filteredChartData.map((item) =>
            item[fieldColumn as keyof typeof item] ? Number(item[fieldColumn as keyof typeof item]) : 0
          )
        );
      }

      if (widgetConfig) {
        return getCustomYScaleDomainValue({
          minChartValue: minValue,
          maxChartValue: maxValue,
          maxScaleValue: widgetConfig.yScaleMax,
          minScaleValue: widgetConfig.yScaleMin,
        });
      } else {
        return [minValue, maxValue];
      }
    }
    return undefined;
  }, [filteredChartData, configurationData]);

  const barChartTicks = generateTicks(barChartYDomainValue);
  const lineChartTicks = generateTicks(linesYDomainScale);

  const onClickLabelListHandler: MouseEventHandler<SVGElement> = useCallback((props) => {
    const target = props.target as Element;

    if (target?.parentElement) {
      onShowModal({ name: target.parentElement.getAttribute('name') ?? '', tooltipPayload: [] });
    }
  }, []);

  const columnLabelPlacement: LabelPosition =
    (configurationData?.widgetConfig?.labelPlacement as LabelPosition) || 'middle';

  const valueAccessor = useCallback(
    (props: CartesianLabelListEntry | PolarLabelListEntry) => CustomValueAccessor(props, legendProps),
    [legendProps]
  );

  const renderBarChartsWithYAxis = useMemo(() => {
    if (!isEmpty(barChartKeys)) {
      const minPointSize = barChartKeys.length === 1 ? MIN_COLUMN_BAR_HEIGHT : 0;

      // Recharts renders bars from bottom to top, so we reverse the order of the keys to ensure the visual stacking order matches the legend and expected display.
      const reversedBarChartKeys = [...barChartKeys].reverse();

      const visibleKeys = reversedBarChartKeys.filter((key) => !legendProps[key]);
      return (
        <>
          <YAxis
            {...yAxisSettings(barChartYDomainValue, 'Quantity', 0, -40, 'left', 'number', 'barYAxis')}
            ticks={barChartTicks}
          />
          {reversedBarChartKeys.map((uniqueDataKey, index) => {
            const lastVisibleKey = visibleKeys.length > 0 ? visibleKeys[visibleKeys.length - 1] : undefined;
            const isLastVisibleBar = uniqueDataKey === lastVisibleKey;
            return (
              <Bar
                key={uniqueDataKey}
                name={uniqueDataKey}
                dataKey={uniqueDataKey}
                stackId="left"
                fill={findCustomColor(uniqueDataKey, colors) ?? getStatusColor(index)}
                hide={legendProps[uniqueDataKey]}
                isAnimationActive={false}
                onClick={createColumnChartBarClickHandler(onShowModal)}
                onMouseEnter={() => handleMouseEnter(uniqueDataKey)}
                onMouseLeave={handleMouseLeave}
                yAxisId="barYAxis"
                cursor="pointer"
                minPointSize={minPointSize}
              >
                {isLastVisibleBar && visibleKeys.length > 1 && (
                  <LabelList
                    position="top"
                    fill="#000"
                    fontSize={11}
                    fontWeight={500}
                    accumulate="sum"
                    additive="replace"
                    cursor="pointer"
                    onClick={onClickLabelListHandler}
                    valueAccessor={valueAccessor}
                  />
                )}
                {transformedData &&
                  transformedData.map((entry, index) => {
                    const predefinedColor = colors && entry.name && findCustomColor(entry.name, colors);
                    return predefinedColor && <Cell key={`cell-${index}`} fill={predefinedColor} />;
                  })}
                <LabelList
                  dataKey={uniqueDataKey}
                  position={columnLabelPlacement}
                  content={wrapPayloadChartContent(CustomMiddleLabelList)}
                />
              </Bar>
            );
          })}
        </>
      );
    }
    return null;
  }, [
    barChartKeys,
    transformedData,
    barChartTicks,
    colors,
    legendProps,
    valueAccessor,
    barChartYDomainValue,
    columnLabelPlacement,
    onClickLabelListHandler,
  ]);

  const handleRefetch = useInvalidateChartResponse(widgetId);

  const ColumChartCustomizedLabel = useCallback(
    (props: unknown) => CustomizedLabel(transformedData as FieldDataProps[], props as ToolTipElement),
    [transformedData]
  );

  const sortLegendItems = useCallback((payload: ReadonlyArray<LegendPayload> | undefined) => {
    const barItems = (payload ?? []).filter((item) => item.type === 'rect');
    const otherItems = (payload ?? []).filter((item) => item.type !== 'rect');

    return [...barItems.reverse(), ...otherItems];
  }, []);

  return (
    <ChartGenericWrapper
      isError={isError}
      isFetching={isWidgetChartDataFetching}
      isLoading={isWidgetChartDataLoading}
      refetch={handleRefetch}
      widgetId={widgetId}
      widgetConfigurationData={configurationData && { ...configurationData, colors, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={shouldSkipRenderingChart}
      error={error}
    >
      {!shouldSkipRenderingChart &&
        configurationData &&
        widgetChartData &&
        transformedData &&
        transformedData.length > 0 && (
          <div ref={chartParentRef} className="h-full">
            <OdinCustomDetailsWindow
              {...configurationData.widgetData}
              widgetId={widgetId}
              fieldValue={modalData?.name}
              secondFieldValue={
                modalData?.tooltipPayload?.[0]?.dataKey !== 'Sum'
                  ? modalData?.tooltipPayload?.[0]?.dataKey
                  : modalData?.Sum
              }
              visible={detailsWindowVisible}
              onDialogHidden={onDialogHidden}
            />

            <ResponsiveContainer height="99%">
              <ComposedChart
                data={transformedData}
                margin={{
                  top: 20,
                  right: 20,
                  left: 20,
                  bottom: xLabelsHeight,
                }}
                layout="horizontal"
                id={widgetId}
              >
                <CartesianGrid
                  yAxisId={renderBarChartsWithYAxis == null && renderYAxisKPIs ? yAxisLineName.current : 'barYAxis'}
                  vertical={false}
                  horizontal
                  strokeDasharray={undefined}
                />

                <Tooltip content={<CustomTooltip />} cursor={false} isAnimationActive={false} />
                <XAxis
                  {...xAxisSettings}
                  interval={intervalCounter(transformedData)}
                  tick={(props) => {
                    return basicValue.length > 0 ? <CustomXAxis {...props} /> : <XAxisLineChart {...props} />;
                  }}
                />
                {renderYAxisKPIs && (
                  <YAxis
                    {...yAxisSettings(
                      linesYDomainScale,
                      'Frequency Rate',
                      0,
                      40,
                      'right',
                      'number',
                      yAxisLineName.current
                    )}
                    allowDataOverflow={false}
                    ticks={lineChartTicks}
                    domain={linesYDomainScale}
                  />
                )}
                {includeLegend(configurationData) && (
                  <Legend
                    wrapperStyle={defaultLegendWrapperStyle}
                    content={(props) => {
                      return (
                        <CustomLegendWithCustomScroll
                          {...props}
                          payload={sortLegendItems(props.payload)}
                          clickListener={selectItem}
                          recalculateLabelsHeight={recalculateLabelsHeight}
                        />
                      );
                    }}
                  />
                )}
                {renderBarChartsWithYAxis}
                {/* KPI */}
                {renderKPILine && (
                  <Line
                    {...{ stackId: 'right' }}
                    name={kpiConfigs.caption}
                    connectNulls
                    type="linear"
                    dataKey="value_KPI"
                    yAxisId={yAxisLineName.current}
                    strokeWidth={2}
                    stroke={kpiConfigs.lineColor}
                    fill={kpiConfigs.lineColor}
                    hide={legendProps.value_KPI}
                    dot={getDotWithColor(kpiConfigs.lineColor)}
                    activeDot={<ActiveDot item={kpiConfigs.caption} />}
                    isAnimationActive={false}
                  >
                    <LabelList dataKey="value_KPI" position="top" content={ColumChartCustomizedLabel} />
                  </Line>
                )}
                {/* rollingTRIFR */}
                {renderTrifrLine && (
                  <Line
                    {...{ stackId: 'right' }}
                    name={trifrConfig.caption}
                    type="linear"
                    dataKey="value_TRIFR"
                    yAxisId={yAxisLineName.current}
                    strokeWidth={2}
                    stroke={trifrConfig.lineColor}
                    fill={trifrConfig.lineColor}
                    hide={legendProps.value_TRIFR}
                    isAnimationActive={false}
                    dot={getDotWithColor(trifrConfig.lineColor)}
                    activeDot={<ActiveDot item={trifrConfig.caption} />}
                  >
                    {trifrConfig.displayValues && (
                      <LabelList
                        dataKey="value_TRIFR"
                        position="top"
                        content={wrapToolTipElementContent(renderCustomizedLineLabel)}
                      />
                    )}
                  </Line>
                )}
                {/* rollingFR */}
                {renderRollingFR &&
                  rollingFRKeys.map((item, index: number) => {
                    return (
                      <Line
                        {...{ stackId: 'right' }}
                        key={index}
                        name={frConfig.legend[index]}
                        type="linear"
                        dataKey="value_FR"
                        yAxisId={yAxisLineName.current}
                        strokeWidth={2}
                        stroke={frConfig.colors[index]}
                        fill={frConfig.colors[index]}
                        hide={legendProps.value_FR}
                        isAnimationActive={false}
                        dot={getDotWithColor(frConfig.colors[index])}
                        activeDot={<ActiveDot item={frConfig.legend[index]} />}
                      >
                        {frConfig.displayValues && (
                          <LabelList
                            dataKey="value_FR"
                            position="top"
                            content={wrapToolTipElementContent(renderCustomizedLineLabel)}
                          />
                        )}
                      </Line>
                    );
                  })}
                {/* rollingSiteTRIFR */}
                {renderRollingSiteTRIFR &&
                  rollingSiteTRIFRKeys.map((item, index: number) => {
                    return (
                      <Line
                        {...{ stackId: 'right' }}
                        key={`${item}_rollingSiteTRIFR`} // must be unique from the Bar keys
                        name={item || 'TRIFR'}
                        dataKey={`${item}_rollingSiteTRIFR`}
                        yAxisId={yAxisLineName.current}
                        strokeWidth={2}
                        type="linear"
                        stroke={getStatusColor(index)}
                        fill={getStatusColor(index)}
                        hide={legendProps[`${item}_rollingSiteTRIFR`]}
                        dot={getDotWithColor(getStatusColor(index))}
                        activeDot={<ActiveDot item={`${item}_rollingSiteTRIFR`} />}
                        isAnimationActive={false}
                      >
                        <LabelList
                          dataKey={`${item}_rollingSiteTRIFR`}
                          position="top"
                          content={wrapToolTipElementContent(renderCustomizedLineLabel)}
                        />
                      </Line>
                    );
                  })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
    </ChartGenericWrapper>
  );
}
